import { createAsyncThunk, current } from '@reduxjs/toolkit';

// types
import { ContextMenuItem } from '../menus/context-menu';
import {
  createAppAsyncThunk,
  DataItemType,
  NavigateEventContext,
  Site,
  SiteStorageArea,
  StorageArea,
} from '../../utils/types';
import {
  Event,
  EventType,
  EventTarget,
  MoveDataToStorage,
  AddCollectionEvent,
  APIStatusParams,
} from '../../utils/events';

// functions
import { useTranslation } from 'react-i18next';
import { APIPrefix, extractFileFromObsID, siteName } from '../../utils/functions';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

import { store } from '../../app/store';

// import state definition and functions.
import {
  CONTEXT_SYSTEMS,
  CONTEXT_MENU_DATA_STAGING as CONTEXT_MENU,
  ctx_setContextMenuDefinition,
} from '../menus/context-menu-slice';
import { ProjectState, setContextMenuRefresh, setProjectItems } from './project-slice';
import { GlobalState } from '../../app/global-slice';
import { DataState } from '../../app/data-slice';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	F U N C T I O N S
//
//	--------------------------------------------------------------------------

//	------------------------------------------------------------
//
//	Rebuild the context menu for staging selected items.
//
//	------------------------------------------------------------

export function buildContextMenu(args: { menuID: CONTEXT_MENU; parentRowID: number }) {
  //
  const stateFetch: any = store.getState();
  const state: ProjectState = stateFetch.project;
  const stateData: DataState = stateFetch.data;

  // store the items so we can return them from the function.
  let items: ContextMenuItem[] = [];

  // rebuild the main menu.
  if (args.menuID === CONTEXT_MENU.MAIN) {
    //
    // define a separator.
    const separator: ContextMenuItem = {
      id: '',
      text: '---',
      childMenu: CONTEXT_MENU.NONE,
    };

    // move to is always there.
    const moveTo: ContextMenuItem = {
      id: 'mnu-main-moveto',
      text: 'Stage to',
      childMenu: CONTEXT_MENU.SITES,
      onClick: false,
      enabled: state.selectedDivs.length > 0,
    };
    items.push(moveTo);

    // update the context menu definition.
    store.dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.PROJECT_DATA_FILES,
        contextMenu: args.menuID,
        items: items,
        parentRowID: args.parentRowID,
      }),
    );
    //
  }

  // update the sites menu.
  if (args.menuID === CONTEXT_MENU.SITES) {
    //
    items = stateData.storageAreas.map((item) => ({
      id:
        'menu-node-' + item.site.node?.toLowerCase() ??
        '' + '-site-' + item.site.site?.toLowerCase() ??
        '',
      text: siteName({ site: item.site }),
      childMenu: CONTEXT_MENU.STORAGES,
      onClick: false,
      enabled: true,
    }));
    store.dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.PROJECT_DATA_FILES,
        contextMenu: args.menuID,
        items: items,
        parentRowID: args.parentRowID,
      }),
    );
    //
  }

  // if we've entered a menu item on the sites context menu then
  // rebuild the storage areas menu.
  if (args.menuID === CONTEXT_MENU.STORAGES) {
    //
    // update the state.
    items = stateData.storageAreas[args.parentRowID].storage_areas.map(
      (item: StorageArea) => ({
        id: 'menu-storage-' + item.storage_id,
        text: item.name !== '' ? item.name : '<' + item.relative_path + '>',
        childMenu: CONTEXT_MENU.NONE,
        onClick: true,
        enabled: true,
      }),
    );
    store.dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.PROJECT_DATA_FILES,
        contextMenu: args.menuID,
        items: items,
        parentRowID: args.parentRowID,
      }),
    );
    //
  }

  // refresh the menu.
  store.dispatch(setContextMenuRefresh(Date.now()));

  // return the items.
  return items;
  //
} // buildContextMenu

//  ------------------------------------------------------------
//
//  Handle actions on the project page.
//
//  ------------------------------------------------------------

export const eventHandler = createAppAsyncThunk(
  'home/homeEventHandler',
  async (args: { event: Event; ctx?: NavigateEventContext }, thunkAPI) => {
    //
    switch (args.event.eventType) {
      case EventType.INITIATE_PROJECT_FILES_SEARCH:
        thunkAPI.dispatch(getProjectFiles());
    }
    //
  },
); // eventHandler

//	------------------------------------------------------------
//
//	An asynchronous function that retrieves a list of project
//	files from the TAP server.
//
//	------------------------------------------------------------

export const getProjectFiles = createAsyncThunk(
  'file_browser/getProjectFiles',
  async (_, thunkAPI) => {
    //
    const stateFetch: any = thunkAPI.getState();
    const state: ProjectState = stateFetch.project;
    const globalState: GlobalState = stateFetch.global;

    let urlCommand: string = APIPrefix() + '/v1/data/get_data?';

    // add the data-product subtype.
    urlCommand =
      urlCommand + 'project=' + encodeURIComponent(globalState.project?.name ?? '') + '&';

    if (state.filenameFilter.trim() !== '') {
      urlCommand =
        urlCommand + 'filename=' + encodeURIComponent(state.filenameFilter) + '&';
    }

    // add page and page size.
    urlCommand = urlCommand + 'page=0&page_size=1000';

    try {
      //
      const apiResult = await fetch(urlCommand, { credentials: 'include' });
      const returnedJson = await apiResult.json();

      // get response.
      let results: any = [];
      if (returnedJson.results !== undefined) {
        results = returnedJson.results;
      }

      // Handle empty results
      if (results.length === 0) {
        //
        // store empty results.
        store.dispatch(setProjectItems([]));
        return;
        //
      }

      // loop over all the rows.
      let dataItems: DataItemType[] = [];
      if (Array.isArray(results) === true) {
        //
        results.forEach((element: any) => {
          //
          let fileDetails: {
            namespace: string;
            filename: string;
          } = extractFileFromObsID({
            obsID: element.obs_id ?? '',
          });
          let newDataItem: DataItemType = {
            id: -1,
            namespace: fileDetails.namespace,
            filename: fileDetails.filename,
            obs_publisher_did: element.obs_publisher_did ?? '',
            did_type: element.dataproduct_type ?? '',
          };
          dataItems.push(newDataItem);
          //
        });
        //
      }

      // store these data items.
      store.dispatch(setProjectItems(dataItems));
      //
    } catch (e) {
      console.error('Error fetching cone search data:', e);
    }
    //
  },
  //
); // getProjectFiles

//	------------------------------------------------------------
//
//	handle the click event for a context menu item.
//
//	------------------------------------------------------------

export function onContextMenuClick(args: { menuID: CONTEXT_MENU; rowID: number[] }) {
  //
  const stateFetch: any = store.getState();
  const state: ProjectState = stateFetch.project;
  const stateData: DataState = stateFetch.data;

  // if we've clicked on a metadata item on the meta data menu
  // then display that item.
  if (args.menuID === CONTEXT_MENU.STORAGES) {
    //
    // build a list of selected files.
    let filesToMove: DataItemType[] = [];

    // add the selected items.
    if (state.selectedItems.length > 0) {
      filesToMove = state.selectedItems.map((map) => {
        return {
          namespace: map.namespace,
          filename: map.filename,
          did_type: 'FILE',
          bytes: 0,
        };
      });
    } else {
      filesToMove = state.projectItems.map((map) => {
        return {
          namespace: map.namespace,
          filename: map.filename,
          did_type: 'FILE',
          bytes: 0,
        };
      });
    }

    const siteIndex: number = args.rowID[CONTEXT_MENU.SITES];
    const storageAreaIndex: number = args.rowID[CONTEXT_MENU.STORAGES];

    // moving data to a storage location.
    const moveDataToStorage: MoveDataToStorage = {
      toStorageAreaUUID:
        stateData.storageAreas[siteIndex].storage_areas[storageAreaIndex].storage_id,
      toSiteUUID: stateData.storageAreas[siteIndex].site.site_uuid ?? '',
      toStorageIdentifier:
        stateData.storageAreas[siteIndex].storage_areas[storageAreaIndex].name,
      lifetime: 3600,
      filesToMove: filesToMove,
    };
    const newEvent: Event = {
      eventTarget: EventTarget.APP,
      eventType: EventType.MOVE_DATA_TO_STORAGE,
      parameters: moveDataToStorage,
    };
    store.dispatch(handleEvent({ event: newEvent }));

    //store.dispatch(setSubmittingJob(true));
    //
  }
  //
} // onContextMenuClick
